import { useEffect, useState } from "react";
import { Link, NavLink, useLocation } from "react-router-dom";
import { Menu, X, Phone } from "lucide-react";
import { AnimatePresence, motion } from "framer-motion";
import { NAV_LINKS, BUSINESS } from "@/data/site";
import { CTAButton } from "@/components/CTAButton";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const location = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [location.pathname]);

  return (
    <>
      {/* Announcement bar */}
      <div
        className="bg-brand-ink text-white text-center text-xs md:text-sm tracking-wide py-2 px-4"
        data-testid="announcement-bar"
      >
        <span className="opacity-90">
          Now serving Sector xxx, Gurugram · {BUSINESS.hoursShort}
        </span>
      </div>

      <header
        className={`sticky top-0 z-50 transition-colors duration-300 border-b ${
          scrolled
            ? "bg-brand-cream/80 backdrop-blur-xl border-brand-ink/10"
            : "bg-transparent border-transparent"
        }`}
        data-testid="site-navbar"
      >
        <nav className="mx-auto flex max-w-7xl items-center justify-between px-5 md:px-8 h-16 md:h-20">
          <Link
            to="/"
            className="flex items-center gap-2 group"
            data-testid="nav-logo"
          >
            <span className="font-display text-xl md:text-2xl font-bold text-brand-ink leading-none">
              Chopstick
            </span>
            <span className="hidden sm:inline text-[10px] uppercase tracking-[0.28em] text-brand-red font-medium pt-1">
              Chinese Corner
            </span>
          </Link>

          <ul className="hidden lg:flex items-center gap-8">
            {NAV_LINKS.map((l) => (
              <li key={l.to}>
                <NavLink
                  to={l.to}
                  data-testid={`nav-link-${l.label.toLowerCase()}`}
                  className={({ isActive }) =>
                    `relative text-sm tracking-wide transition-colors duration-300 hover:text-brand-red ${
                      isActive ? "text-brand-red" : "text-brand-ink/80"
                    }`
                  }
                >
                  {l.label}
                </NavLink>
              </li>
            ))}
          </ul>

          <div className="hidden lg:block">
            <CTAButton href={`tel:${BUSINESS.phoneHref}`} variant="primary" icon={false} data-testid="nav-call-btn">
              <Phone size={15} /> Call to book
            </CTAButton>
          </div>

          <button
            className="lg:hidden inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-ink/15 text-brand-ink"
            onClick={() => setOpen(true)}
            aria-label="Open menu"
            data-testid="mobile-menu-open"
          >
            <Menu size={20} />
          </button>
        </nav>
      </header>

      {/* Mobile menu */}
      <AnimatePresence>
        {open && (
          <motion.div
            className="fixed inset-0 z-[70] lg:hidden"
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            data-testid="mobile-menu-overlay"
          >
            <div
              className="absolute inset-0 bg-brand-ink/60 backdrop-blur-sm"
              onClick={() => setOpen(false)}
            />
            <motion.div
              className="absolute right-0 top-0 h-full w-[82%] max-w-sm bg-brand-cream p-6 flex flex-col"
              initial={{ x: "100%" }}
              animate={{ x: 0 }}
              exit={{ x: "100%" }}
              transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
            >
              <div className="flex items-center justify-between mb-10">
                <span className="font-display text-2xl font-bold text-brand-ink">
                  Menu
                </span>
                <button
                  className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-brand-ink/15"
                  onClick={() => setOpen(false)}
                  aria-label="Close menu"
                  data-testid="mobile-menu-close"
                >
                  <X size={20} />
                </button>
              </div>
              <ul className="flex flex-col gap-1">
                {NAV_LINKS.map((l) => (
                  <li key={l.to}>
                    <NavLink
                      to={l.to}
                      data-testid={`mobile-nav-link-${l.label.toLowerCase()}`}
                      className={({ isActive }) =>
                        `block font-display text-3xl py-2 transition-colors ${
                          isActive ? "text-brand-red" : "text-brand-ink"
                        }`
                      }
                    >
                      {l.label}
                    </NavLink>
                  </li>
                ))}
              </ul>
              <div className="mt-auto pt-8">
                <CTAButton
                  href={`tel:${BUSINESS.phoneHref}`}
                  variant="primary"
                  className="w-full"
                  icon={false}
                >
                  <Phone size={16} /> {BUSINESS.phoneDisplay}
                </CTAButton>
              </div>
            </motion.div>
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
